import React from "react";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { RefreshCcw, RotateCcw, Sparkles } from "lucide-react";
import { toast } from "sonner";

import tutorialSteps from "@/data/tutorialSteps";
import { useAuth } from "@/contexts/AuthContext";
import {
    useTutorialStatus,
    useResetTutorialStatus,
} from "@/queries/account";
import {
    getStatusStorageKey,
    getIntroStorageKey,
    writeTutorialStatus,
    writeIntroCompleted,
    clearIntroCompleted,
} from "@/utils/tutorialStorage";
import { getNativePlatform } from "@/lib/runtime";
import { NativeSettingsSkeleton } from "@/components/skeletons/NativeLoadingSkeletons";

const PAGE_CONFIG = [
    {
        key: "homeOnboarding",
        title: "Hitta i appen",
        description: "Visar den korta navigationsguiden på startsidan efter introduktionen.",
    },
    {
        key: "matches",
        title: "Matcher",
        description: "Visar de aktuella matchkorten tillsammans med seriens kommande matcher.",
    },
    {
        key: "myMatches",
        title: "Mina matchprotokoll",
        description: "Hjälper dig hitta tillbaka till påbörjade och sparade protokoll via Mina sidor.",
    },
    {
        key: "officialMatchDetail",
        title: "Matchkort",
        description: "Guidar dig genom matchkortet innan du går vidare till protokolläget.",
    },
    {
        key: "matchProtocol",
        title: "Matchprotokoll",
        description: "Visar hur du navigerar mellan heat och öppnar registreringen i protokolläget.",
    },
    {
        key: "heatDialog",
        title: "Heatdialog",
        description: "Hjälper dig välja inmatningsläge, registrera resultat, hantera byten och spara heat.",
    },
    {
        key: "protocolWidget",
        title: "Protokollwidget",
        description: "Guidar hur den flyttbara widgeten fungerar och hur du återupptar protokoll.",
    },
];

function TutorialStatusBadge({ completed }) {
    if (completed) {
        return <Badge variant="secondary" className="bg-emerald-500/10 text-emerald-500">Genomförd</Badge>;
    }
    return <Badge variant="outline" className="w-fit flex-none whitespace-nowrap">Inte visad</Badge>;
}

export default function TutorialsPanel() {
    const { user } = useAuth() || {};
    const { data, isLoading, isError } = useTutorialStatus();
    const resetMutation = useResetTutorialStatus();

    const status = React.useMemo(
        () => ({
            introCompleted: Boolean(data?.tutorial?.introCompleted),
            pages: data?.tutorial?.pages || {},
        }),
        [data]
    );

    const storageKey = React.useMemo(
        () => (user ? getStatusStorageKey(user) : null),
        [user]
    );
    const introKey = React.useMemo(
        () => (user ? getIntroStorageKey(user) : null),
        [user]
    );

    React.useEffect(() => {
        if (!data?.tutorial) return;
        if (!storageKey) return;
        writeTutorialStatus(storageKey, status);
        if (!introKey) return;
        if (status.introCompleted) {
            writeIntroCompleted(introKey);
        } else {
            clearIntroCompleted(introKey);
        }
    }, [data, introKey, status, storageKey]);

    const handleReset = React.useCallback(
        async (payload, message) => {
            await toast.promise(resetMutation.mutateAsync(payload), {
                loading: "Uppdaterar…",
                success: message,
                error: (err) => err?.message || "Kunde inte uppdatera tutorialstatus.",
            });
        },
        [resetMutation]
    );

    const introActions = (
        <Button
            variant="outline"
            size="sm"
            onClick={() => handleReset({ resetIntro: true }, "Introduktionen visas nästa gång du loggar in.")}
            disabled={resetMutation.isPending || !status.introCompleted}
        >
            <RefreshCcw className="h-4 w-4 mr-2" />Visa intro igen
        </Button>
    );

    const resetAllActions = (
        <Button
            variant="secondary"
            size="sm"
            onClick={() => handleReset({ resetAllPages: true, resetIntro: true }, "Alla guider återställs och kan spelas upp igen.")}
            disabled={resetMutation.isPending}
        >
            <RotateCcw className="h-4 w-4 mr-2" />Återställ alla guider
        </Button>
    );

    if (isLoading && getNativePlatform() === "android") {
        return <NativeSettingsSkeleton sections={3} />;
    }

    return (
        <div className="space-y-4">
            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <Sparkles className="h-5 w-5 shrink-0 opacity-70" />
                        <span>Introduktion</span>
                    </CardTitle>
                    <CardDescription>
                        Första gången du loggar in visas en kort intro-tutorial. Du kan trigga den igen här.
                    </CardDescription>
                </CardHeader>

                {isLoading ? (
                    <CardContent className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                        <Skeleton className="h-4 w-24 mb-2" />
                        <div className="flex gap-2">
                            <Skeleton className="h-7 w-48" />
                            <Skeleton className="h-7 w-48" />
                        </div>
                    </CardContent>
                ) : (
                    <CardContent className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                        <TutorialStatusBadge completed={status.introCompleted} />
                        <div className="flex flex-wrap items-center justify-center gap-2 sm:justify-start">
                            {introActions}
                            {resetAllActions}
                        </div>
                    </CardContent>
                )}
            </Card>

            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <Sparkles className="h-5 w-5 shrink-0 opacity-70" />
                        <span>Snabbguider</span>
                    </CardTitle>
                    <CardDescription>
                        Återställ enskilda guider om du vill se dem igen nästa gång du öppnar rätt vy eller flöde.
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                    {isLoading ? (
                        <>
                            {PAGE_CONFIG.map((item) => (
                                <div key={item.key} className="flex flex-col gap-2 rounded-lg border border-border/60 p-3 sm:flex-row sm:items-center sm:justify-between">
                                    <div className="flex-1">
                                        <div className="flex justify-between items-center mb-2 gap-2">
                                            <Skeleton className="h-4 w-24 mb-2" />
                                            <Skeleton className="h-4 w-24 mb-2" />
                                        </div>
                                        <Skeleton className="h-3 w-auto mb-2" />
                                        <Skeleton className="h-3 w-48 mb-2" />
                                        <Skeleton className="h-7 w-full" />
                                    </div>
                                </div>
                            ))}
                        </>
                    ) : (
                        PAGE_CONFIG.filter((item) => Array.isArray(tutorialSteps[item.key])).map((item) => {
                            const completed = Boolean(status.pages?.[item.key]?.completed);
                            return (
                                <div
                                    key={item.key}
                                    className="flex flex-col gap-2 rounded-lg border border-border/60 p-3 sm:flex-row sm:items-center sm:justify-between"
                                >
                                    <div>
                                        <div className="font-medium flex items-center gap-2 justify-between sm:justify-start">
                                            {item.title}
                                            <TutorialStatusBadge completed={completed} />
                                        </div>
                                        <p className="text-sm text-muted mt-1 max-w-2xl">
                                            {item.description}
                                        </p>
                                    </div>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => handleReset({ pageName: item.key }, `Guiden för ${item.title} startar igen nästa gång du öppnar rätt vy.`)}
                                        disabled={resetMutation.isPending}
                                    >
                                        <RefreshCcw className="h-4 w-4 mr-2" />Visa igen
                                    </Button>
                                </div>
                            );
                        })
                    )}
                    {isError && (
                        <p className="text-sm text-red-500">
                            Kunde inte hämta tutorialstatus just nu. Försök igen senare.
                        </p>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}
